import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { cn } from '@/lib/utils';
import {
  Loader2,
  Trophy,
  XCircle,
  AlertCircle,
} from 'lucide-react';

export type DealType = 'Product' | 'Service' | 'Subscription' | 'Other';

interface CloseDealModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  leadId: string;
  leadName: string;
  dealId?: string;
  existingDealValue?: number;
  outcome: 'won' | 'lost';
  onConfirm: (data: {
    deal_value: number;
    deal_type: DealType;
    close_reason?: string;
  }) => void;
}

export function CloseDealModal({
  open,
  onOpenChange,
  leadId,
  leadName,
  dealId,
  existingDealValue,
  outcome,
  onConfirm,
}: CloseDealModalProps) {
  const [dealValue, setDealValue] = useState<string>(existingDealValue?.toString() || '');
  const [dealType, setDealType] = useState<DealType>('Product');
  const [closeReason, setCloseReason] = useState<string>('');
  const [errors, setErrors] = useState<{ dealValue?: string; dealType?: string }>({});

  // Reset form when modal opens/closes or dealId changes
  useEffect(() => {
    if (open) {
      setDealValue(existingDealValue?.toString() || '');
      setDealType('Product');
      setCloseReason('');
      setErrors({});
    }
  }, [open, existingDealValue]);

  const handleSubmit = () => {
    const newErrors: { dealValue?: string; dealType?: string } = {};

    // Validate deal_value
    const value = parseFloat(dealValue);
    if (!dealValue || isNaN(value) || value <= 0) {
      newErrors.dealValue = 'Deal value is required and must be greater than 0';
    }

    // Validate deal_type (should always be set, but double-check)
    if (!dealType) {
      newErrors.dealType = 'Please select a deal type';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Clear errors and submit
    setErrors({});
    onConfirm({
      deal_value: value,
      deal_type: dealType,
      close_reason: outcome === 'lost' ? closeReason : undefined,
    });
  };

  const isWon = outcome === 'won';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-base font-medium flex items-center gap-2">
            {isWon ? (
              <>
                <Trophy className="w-5 h-5 text-emerald-500" />
                Close Deal - Won
              </>
            ) : (
              <>
                <XCircle className="w-5 h-5 text-slate-500" />
                Close Deal - Lost
              </>
            )}
          </DialogTitle>
          <p className="text-sm text-muted-foreground">{leadName}</p>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Deal Value - Required */}
          <div className="space-y-2">
            <Label htmlFor="deal-value" className="text-sm font-medium">
              Deal Value <span className="text-destructive">*</span>
            </Label>
            <Input
              id="deal-value"
              type="number"
              min="0.01"
              step="0.01"
              value={dealValue}
              onChange={(e) => {
                setDealValue(e.target.value);
                if (errors.dealValue) {
                  setErrors({ ...errors, dealValue: undefined });
                }
              }}
              placeholder="Enter deal value"
              className={cn(errors.dealValue && "border-destructive")}
            />
            {errors.dealValue && (
              <p className="text-xs text-destructive flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                {errors.dealValue}
              </p>
            )}
            {existingDealValue && (
              <p className="text-xs text-muted-foreground">
                Previous value: {existingDealValue.toLocaleString('en-US', { style: 'currency', currency: 'EUR' })}
              </p>
            )}
          </div>

          {/* Deal Type - Required */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">
              Deal Type <span className="text-destructive">*</span>
            </Label>
            <RadioGroup
              value={dealType}
              onValueChange={(v) => {
                setDealType(v as DealType);
                if (errors.dealType) {
                  setErrors({ ...errors, dealType: undefined });
                }
              }}
              className="space-y-2"
            >
              {(['Product', 'Service', 'Subscription', 'Other'] as DealType[]).map((type) => (
                <Label
                  key={type}
                  className={cn(
                    "flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors",
                    dealType === type
                      ? "border-primary bg-primary/5"
                      : "border-border hover:border-muted-foreground/30"
                  )}
                >
                  <RadioGroupItem value={type} />
                  <span className="text-sm font-medium">{type}</span>
                </Label>
              ))}
            </RadioGroup>
            {errors.dealType && (
              <p className="text-xs text-destructive flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                {errors.dealType}
              </p>
            )}
          </div>

          {/* Close Reason - Optional, only for Lost */}
          {!isWon && (
            <div className="space-y-2 pt-2 border-t border-border">
              <Label htmlFor="close-reason" className="text-sm font-medium">
                Reason (optional)
              </Label>
              <Input
                id="close-reason"
                value={closeReason}
                onChange={(e) => setCloseReason(e.target.value)}
                placeholder="e.g., Price, Timing, Competitor"
              />
              <p className="text-xs text-muted-foreground">
                Help us understand why this deal was lost
              </p>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-2 border-t border-border">
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={handleSubmit}
            className={cn(
              isWon && "bg-emerald-600 hover:bg-emerald-700"
            )}
          >
            {isWon ? (
              <>
                <Trophy className="w-4 h-4 mr-2" />
                Close Won
              </>
            ) : (
              <>
                <XCircle className="w-4 h-4 mr-2" />
                Close Lost
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

